import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { PublicShell } from "@/app/layouts/PublicShell";
import { AppShell } from "@/app/layouts/AppShell";
import { StudyShell } from "@/app/layouts/StudyShell";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { RedirectIfAuthed } from "@/components/auth/RedirectIfAuthed";
import { HomeRedirect } from "@/components/auth/HomeRedirect";
import { LoginPage } from "@/pages/auth/LoginPage";
import { VerifyEmailPage } from "@/pages/auth/VerifyEmailPage";
import { ForgotPasswordPage } from "@/pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "@/pages/auth/ResetPasswordPage";
import { ContinueWithEmailPage } from "@/pages/auth/ContinueWithEmailPage";
import { OnboardingPage } from "@/pages/OnboardingPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { SubjectsPage } from "@/pages/SubjectsPage";
import { ChaptersPage } from "@/pages/ChaptersPage";
import { StudyModePage } from "@/pages/study/StudyModePage";
import { TextbookPage } from "@/pages/study/TextbookPage";
import { NotesPage } from "@/pages/study/NotesPage";
import { QuestionBankPage } from "@/pages/study/QuestionBankPage";
import { SlidesPage } from "@/pages/study/SlidesPage";
import { PodcastPage } from "@/pages/study/PodcastPage";
import { VideoPage } from "@/pages/study/VideoPage";
import { FlashcardsPage } from "@/pages/study/FlashcardsPage";
import { MindMapPage } from "@/pages/study/MindMapPage";
import { QuizPage } from "@/pages/study/QuizPage";
import { TestTakePage } from "@/pages/study/TestTakePage";
import { TestsInboxPage } from "@/pages/TestsInboxPage";
import { StyleguidePage } from "@/pages/StyleguidePage";
import { ChatPage } from "@/pages/ChatPage";
import { SubscribePage } from "@/pages/SubscribePage";
import { SubscribeSuccessPage } from "@/pages/SubscribeSuccessPage";
import { BillingPage } from "@/pages/BillingPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { NotFound } from "@/pages/NotFound";
import {
  ContactPage,
  PrivacyPage,
  RefundPolicyPage,
  ShippingPolicyPage,
  TermsPage,
} from "@/pages/legal/LegalPages";

// Bundle-isolation boundary (ADR-1, docs/PARENT_PORTAL_PLAN.md): the entire
// parent portal -- its shell, pages and components -- loads through this one
// lazy entry point. Parents never download student-page chunks and students
// never download this one. `.then(m => ({default: m.ParentLayout}))` keeps
// the named-exports-only convention instead of adding a default export.
const ParentLayout = lazy(() =>
  import("@/pages/parent/ParentLayout").then((m) => ({ default: m.ParentLayout })),
);

function ParentPortalFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center text-text-muted text-sm">
      Loading…
    </div>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Internal-only design proposals page. Not linked from any nav, and
          gated behind sign-in so it isn't publicly reachable by URL. */}
      <Route
        path="/styleguide"
        element={
          <RequireAuth>
            <StyleguidePage />
          </RequireAuth>
        }
      />

      {/* `/` redirects to the demo gateway -- the user keeps their existing
          marketing site at staxed.in, so the SPA root just points anonymous
          visitors at the free-chapter browsing experience. Authenticated
          users get bounced on to /dashboard from /subjects via that page.
          A signed-in parent goes to /parent instead (see HomeRedirect). */}
      <Route path="/" element={<HomeRedirect />} />

      {/* Public shell: auth screens + public legal/compliance pages. The marketing landing lives separately
          at staxed.in -- the v2 SPA does not ship its own landing. Each
          auth screen is wrapped in <RedirectIfAuthed> so already-signed-in
          users are bounced to ?next= (or /dashboard) instead of seeing
          stale login chrome. Verify/reset stay reachable while signed in
          (signed-in flows can still need verification). */}
      <Route element={<PublicShell />}>
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/refund-policy" element={<RefundPolicyPage />} />
        <Route path="/shipping-policy" element={<ShippingPolicyPage />} />
        <Route
          path="/auth/login"
          element={<RedirectIfAuthed><LoginPage /></RedirectIfAuthed>}
        />
        {/* /auth/signup is kept as an alias of /auth/otp for old links (the
            paywall CTA, the marketing site) -- both now render the same
            unified continue-with-email flow, which handles sign-up too. */}
        <Route
          path="/auth/signup"
          element={<RedirectIfAuthed><ContinueWithEmailPage /></RedirectIfAuthed>}
        />
        <Route
          path="/auth/otp"
          element={<RedirectIfAuthed><ContinueWithEmailPage /></RedirectIfAuthed>}
        />
        <Route
          path="/auth/forgot"
          element={<RedirectIfAuthed><ForgotPasswordPage /></RedirectIfAuthed>}
        />
        <Route
          path="/auth/reset"
          element={<RedirectIfAuthed><ResetPasswordPage /></RedirectIfAuthed>}
        />
        <Route path="/auth/verify" element={<VerifyEmailPage />} />
      </Route>

      {/* Authenticated app shell ----------------------------------------- */}
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/subscribe" element={<SubscribePage />} />
        <Route path="/subscribe/success" element={<SubscribeSuccessPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/tests" element={<TestsInboxPage />} />
        <Route path="/admin/*" element={<AdminLayout />} />
      </Route>

      {/* Parent portal -- own shell (ParentShell, mounted inside
          ParentLayout), not AppShell. Single lazy chunk (see the ParentLayout
          import above). */}
      <Route
        path="/parent/*"
        element={
          <RequireAuth>
            <Suspense fallback={<ParentPortalFallback />}>
              <ParentLayout />
            </Suspense>
          </RequireAuth>
        }
      />

      {/* Subjects / chapters / mode picker - browsing surfaces, viewable
          by anonymous (demo) and authed. The picker keeps the bottom nav
          so users can hop to dashboard / subjects / chat without losing
          their place; only the actual viewers go immersive. */}
      <Route element={<AppShell />}>
        <Route path="/subjects" element={<SubjectsPage />} />
        <Route path="/subjects/:subjectId/chapters" element={<ChaptersPage />} />
        <Route path="/study/:chapterId" element={<StudyModePage />} />
      </Route>

      {/* Study shell: immersive viewers --------------------------------- */}
      <Route element={<StudyShell />}>
        <Route path="/study/:chapterId/textbook" element={<TextbookPage />} />
        <Route path="/study/:chapterId/notes" element={<NotesPage />} />
        <Route path="/study/:chapterId/question-bank" element={<QuestionBankPage />} />
        <Route path="/study/:chapterId/slides" element={<SlidesPage />} />
        <Route path="/study/:chapterId/podcast" element={<PodcastPage />} />
        <Route path="/study/:chapterId/video" element={<VideoPage />} />
        <Route path="/study/:chapterId/flashcards" element={<FlashcardsPage />} />
        <Route path="/study/:chapterId/mind-map" element={<MindMapPage />} />
        <Route path="/study/:chapterId/quiz" element={<QuizPage />} />
        <Route path="/tests/:assignmentId/take" element={<TestTakePage />} />
      </Route>

      {/* Compat redirects from the old SPA paths so deep links don't 404 - */}
      <Route path="/onboarding-old" element={<Navigate to="/onboarding" replace />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
